/**
 * The console's configuration, validated once, at import.
 *
 * CLAUDE.md §3: **no fallback values for configuration; a missing value raises.** The
 * server-side rule lives in `packages/config`, which cannot be used here — it reads
 * `process.env` and throws in a browser bundle — so the same rule is restated for the
 * only three values this application needs. There is no `??`, no `||` and no
 * "same origin" guess anywhere below: a console built without an API base URL fails
 * loudly at startup rather than quietly calling itself.
 *
 * AMENDED 2026-09-22 (plan-002 RB-05, design §15.8.1, DD-1, closes C8). The API base URL may
 * also be the literal token `same-origin`, an EXPLICIT statement in the build that `/v1` is
 * served by this console's own nginx, which proxies it to the API inside the environment. It is
 * resolved to `window.location.origin` once, at start-up. That is not the guess the rule above
 * forbids: the guess is about a MISSING value, and a missing value still throws. It exists
 * because a hostname baked into a bundle is wrong after every hostname change (ADR 0016's
 * environment replacement, then `app.wetalk.io`), and the `__Host-` session cookie requires the
 * console and `/v1` to share an origin anyway.
 */

/** Mirrors the `configuration_missing` / `configuration_invalid` codes of the API's taxonomy. */
export class ConsoleConfigurationError extends Error {
  override readonly name = "ConsoleConfigurationError";
  readonly key: string;
  constructor(key: string, detail: string) {
    super(`${key} ${detail}. Set it in the environment the console is built with.`);
    this.key = key;
  }
}

/** The four keys this application reads. Named, so a typo is a type error. */
type ConfigKey =
  | "VITE_WETALK_API_BASE_URL"
  | "VITE_WETALK_CONSOLE_VERSION"
  | "VITE_WETALK_GATEWAY"
  | "VITE_WETALK_PUBLIC_SITE_BASE_URL";

function requiredText(environment: ImportMetaEnv, key: ConfigKey): string {
  const value = environment[key];
  if (value === undefined || value.trim() === "") {
    throw new ConsoleConfigurationError(key, "is not set");
  }
  return value.trim();
}

/** Which gateway the console is wired to. See `src/api/gateway.ts` for what that means. */
export const GATEWAY_KIND = ["api", "sandbox"] as const;
export type GatewayKind = (typeof GATEWAY_KIND)[number];

export interface ConsoleConfig {
  readonly apiBaseUrl: string;
  readonly consoleVersion: string;
  readonly gateway: GatewayKind;
  /**
   * Where `apps/web-public` is served (P12-03).
   *
   * The Orders tab's "Open shop screen" builds a link into the public site, which is a
   * different origin from this one. No default and no same-origin guess: a console that
   * guessed would produce a link to a page it does not serve, and the way a shop owner finds
   * that out is by carrying a tablet to a counter.
   */
  readonly publicSiteBaseUrl: string;
}

/**
 * The explicit token that says "`/v1` is on this console's own origin" (IC-11). The only
 * non-URL value `VITE_WETALK_API_BASE_URL` accepts.
 */
export const SAME_ORIGIN = "same-origin";

/** The one property of `window.location` the console reads. */
export interface PageLocation {
  readonly origin: string;
}

/**
 * The API base URL: an absolute `http(s)://` URL (kept for `pnpm dev` against a local API), or
 * the page's own origin when the build says `same-origin`. Anything else throws.
 */
function apiBaseUrlOf(environment: ImportMetaEnv, page: PageLocation | undefined): string {
  const stated = requiredText(environment, "VITE_WETALK_API_BASE_URL");
  if (stated === SAME_ORIGIN) {
    const origin = page === undefined ? "" : page.origin;
    if (!origin.startsWith("https://") && !origin.startsWith("http://")) {
      throw new ConsoleConfigurationError(
        "VITE_WETALK_API_BASE_URL",
        `is "${SAME_ORIGIN}", but the page has no http(s) origin to resolve it to ` +
          `(received "${origin}")`,
      );
    }
    return origin;
  }
  if (!stated.startsWith("https://") && !stated.startsWith("http://")) {
    throw new ConsoleConfigurationError(
      "VITE_WETALK_API_BASE_URL",
      `must be an absolute http(s) URL or "${SAME_ORIGIN}"; received "${stated}"`,
    );
  }
  return stated;
}

/**
 * Reads and validates. Exported so a suite can drive it without a real environment.
 *
 * `page` is the browser's `window.location`, read at start-up; a suite passes its own. It is
 * consulted only when the build states `same-origin`.
 */
export function readConsoleConfig(
  environment: ImportMetaEnv,
  page: PageLocation | undefined = globalThis.location,
): ConsoleConfig {
  const apiBaseUrl = apiBaseUrlOf(environment, page);

  const gateway = requiredText(environment, "VITE_WETALK_GATEWAY");
  if (!(GATEWAY_KIND as readonly string[]).includes(gateway)) {
    throw new ConsoleConfigurationError(
      "VITE_WETALK_GATEWAY",
      `must be one of ${GATEWAY_KIND.join(" | ")}; received "${gateway}"`,
    );
  }

  const publicSiteBaseUrl = requiredText(environment, "VITE_WETALK_PUBLIC_SITE_BASE_URL");
  if (!publicSiteBaseUrl.startsWith("https://") && !publicSiteBaseUrl.startsWith("http://")) {
    throw new ConsoleConfigurationError(
      "VITE_WETALK_PUBLIC_SITE_BASE_URL",
      `must be an absolute http(s) URL; received "${publicSiteBaseUrl}"`,
    );
  }

  return {
    apiBaseUrl: apiBaseUrl.replace(/\/+$/, ""),
    consoleVersion: requiredText(environment, "VITE_WETALK_CONSOLE_VERSION"),
    gateway: gateway as GatewayKind,
    publicSiteBaseUrl: publicSiteBaseUrl.replace(/\/+$/, ""),
  };
}
